import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { HERO_SLIDES } from '../data/mock';
import { buildImageSrcSet, optimizeImageUrl } from '../lib/image';

const INTERVAL = 5000;
// LCP slide 0: higher quality (visible immediately)
const Q_LCP   = [44, 50, 56, 65, 72];
// Deferred slides 1+: lighter quality. Must stay >= 40 — the /api/image resizer
// rejects q<40 with 422 (which silently broke mobile deferred-slide images).
const Q_DEFER = [40, 42, 46, 52, 58];

const shouldAutoplay = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return !reduced;
};

const shouldAnimateHero = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = window.matchMedia('(min-width: 768px)').matches;
  return !reduced && desktop;
};

const HeroCarousel = () => {
  const [current, setCurrent] = useState(0);
  const animateHero = shouldAnimateHero();
  // Only mount img elements for slides that have been (or are about to be) shown.
  // This prevents the browser from fetching all carousel images at page load,
  // which would compete with the LCP image (slide 0).
  const [mounted, setMounted] = useState(() => new Set([0]));
  const timerRef = useRef(null);
  const touchStartX = useRef(null);

  const mountSlide = useCallback((idx) => {
    setMounted(prev => {
      if (prev.has(idx)) return prev;
      const n = new Set(prev);
      n.add(idx);
      return n;
    });
  }, []);

  const goTo = useCallback((idx) => {
    setCurrent(idx);
    mountSlide(idx);
    mountSlide((idx + 1) % HERO_SLIDES.length);
  }, [mountSlide]);

  const next = useCallback(() => goTo((current + 1) % HERO_SLIDES.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + HERO_SLIDES.length) % HERO_SLIDES.length), [current, goTo]);

  // After LCP is likely done, start preloading slide 1 in background.
  useEffect(() => {
    const t = setTimeout(() => mountSlide(1), 800);
    return () => clearTimeout(t);
  }, [mountSlide]);

  // Auto-advance
  useEffect(() => {
    if (!shouldAutoplay()) return undefined;
    timerRef.current = setInterval(next, INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [next]);

  // Pause on hover
  const pause = () => clearInterval(timerRef.current);
  const resume = () => {
    if (!shouldAutoplay()) return;
    timerRef.current = setInterval(next, INTERVAL);
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    pause();
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) dx < 0 ? next() : prev();
    touchStartX.current = null;
    resume();
  };

  return (
    <section
      className="relative touch-pan-y"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={() => { touchStartX.current = null; resume(); }}
    >
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6">
        <div className="relative overflow-hidden rounded-[24px] bg-[var(--msa-bg-soft)]">
          <div className="relative h-[380px] sm:h-[440px] lg:h-[520px]">

            {/* Slides */}
            {HERO_SLIDES.map((slide, i) => (
              <div
                key={slide.id}
                className={`absolute inset-0 ${animateHero ? 'transition-opacity duration-700' : ''}`}
                style={{
                  opacity: i === current ? 1 : 0,
                  zIndex: i === current ? 2 : 0,
                  pointerEvents: i === current ? 'auto' : 'none',
                }}
                aria-hidden={i !== current}
              >
                {mounted.has(i) && (
                <picture>
                  {slide.mobileImage && (
                    <source
                      media="(max-width: 639px)"
                      srcSet={buildImageSrcSet(slide.mobileImage, [360, 480, 720, 900], i === 0 ? Q_LCP : Q_DEFER)}
                      sizes="100vw"
                    />
                  )}
                  <img
                    src={optimizeImageUrl(slide.image, 960, i === 0 ? 56 : 38)}
                    srcSet={buildImageSrcSet(slide.image, [480, 768, 1080, 1320, 1920], i === 0 ? Q_LCP : Q_DEFER)}
                    sizes="(max-width: 640px) 100vw, (max-width: 1320px) 100vw, 1320px"
                    alt={slide.title}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    decoding={i === 0 ? 'sync' : 'async'}
                    fetchpriority={i === 0 ? 'high' : undefined}
                    width="1600"
                    height="520"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </picture>
                )}
                <div className={`absolute inset-0 ${slide.align === 'left' ? 'bg-gradient-to-r from-black/65 via-black/35 to-transparent' : 'bg-gradient-to-l from-black/65 via-black/35 to-transparent'}`} />
                <div className={`relative h-full max-w-[1180px] mx-auto px-6 sm:px-10 flex items-center ${slide.align === 'right' ? 'justify-end' : ''}`}>
                  <div
                    className={`max-w-[560px] ${animateHero ? 'transition-all duration-700' : ''} ${i === current ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'} ${slide.align === 'right' ? 'text-right' : ''}`}
                  >
                    <span className={`inline-block px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.18em] mb-4 ${slide.accent === 'green' ? 'bg-[#8abf35] text-[#17310f]' : 'bg-[#0f6fb8] text-white'}`}>{slide.eyebrow || 'MSA · формули'}</span>
                    <h2 className="font-display text-white text-[28px] sm:text-[36px] lg:text-[48px] leading-[1.05] font-bold tracking-tight">{slide.title}</h2>
                    <p className="text-white/85 mt-3 sm:mt-4 text-[14px] sm:text-[16px] max-w-[480px]" style={slide.align === 'left' ? { marginLeft: 0 } : {}}>{slide.subtitle}</p>
                    {slide.bullets && (
                      <ul className="mt-3 sm:mt-4 space-y-1.5 max-w-[480px]">
                        {slide.bullets.map((b, k) => (
                          <li key={k} className="flex items-center gap-2 text-white/90 text-[13px] sm:text-[15px]">
                            <span className="w-5 h-5 rounded-full bg-[var(--msa-green-cta)] grid place-items-center shrink-0"><Check className="w-3 h-3 text-white" /></span>
                            {b}
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link
                      to={slide.href}
                      className={`inline-flex items-center gap-2 mt-6 h-12 px-6 rounded-full font-semibold text-sm transition-colors ${slide.accent === 'green' ? 'bg-[var(--msa-green-cta)] hover:bg-[var(--msa-green-cta-hover)] text-white' : 'bg-white hover:bg-[var(--msa-blue-soft)] text-[var(--msa-ink)]'}`}
                    >
                      {slide.cta} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}

            {/* Prev/Next arrows */}
            <button
              onClick={prev}
              className="hidden sm:flex absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/30 hover:bg-black/55 text-white items-center justify-center transition-colors backdrop-blur-sm"
              aria-label="Попередній слайд"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              className="hidden sm:flex absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/30 hover:bg-black/55 text-white items-center justify-center transition-colors backdrop-blur-sm"
              aria-label="Наступний слайд"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Dot indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
              {HERO_SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`transition-all duration-300 rounded-full ${i === current ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/50 hover:bg-white/80'}`}
                  aria-label={`Слайд ${i + 1}`}
                />
              ))}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;
